import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import Permission from '../../components/Permission/Permission';

import { useAlertStore } from '../../store/providers/Alert';
import { useRoyaltyPlanTemplateStore } from '../../store/providers/RoyaltyPlanTemplate';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './RoyaltyPlanTemplateSchema';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));

function RoyaltyPlanTemplates() {
    // Redux state
    const dispatch = useDispatch();

    // Store state
    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, data, clearData, duplicateRecord } = useRoyaltyPlanTemplateStore();

    // Local state
    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);

    const pagePermission = constants.PERMISSION_SETTINGS_ROYALTYPLANS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (royaltyPlanTemplate) => {
        navigate(`/royaltyPlanTemplates/edit/${royaltyPlanTemplate.id}`);
    };

    const addedHandler = () => {
        navigate('/royaltyPlanTemplates/new');
    };

    const deletedHandler = (royaltyPlan) => {
        deleteRecord({ royaltyPlanId: royaltyPlan.id });
    };

    const refreshedHandler = () => {
        listRecords();
    };

    const copiedHandler = (royaltyPlan) => {
        duplicateRecord({ royaltyPlanId: royaltyPlan.id });
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }
        }

        setIsBusy(!_.isEmpty(data) && (data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list')));
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'royaltyPlanTemplates', parent: 'Settings', text: 'Royalty Plans', path: '/royaltyPlanTemplates' }));
        listRecords();

        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="settings" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Royalty Plans" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        compType="Royalty Plan"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hidePrint={true}
                                        hideFilter={true}
                                        permissionEdit={canEdit}
                                        added={() => addedHandler()}
                                        selected={(royaltyPlan) => selectedHandler(royaltyPlan)}
                                        deleted={(royaltyPlan) => deletedHandler(royaltyPlan)}
                                        copied={(royaltyPlan) => copiedHandler(royaltyPlan)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(RoyaltyPlanTemplates));
